import React from "react";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import {
    Grid,
    Typography,
    Paper,
    Stack,
    Button,
    Box,
} from "@mui/material";
import styled from "styled-components";
import selfiePic from "../assets/images/selfie_suit.png";
import "../assets/styles/Main.scss";
import { useNavigate } from 'react-router-dom';


const StyledTypography = styled(Typography)(() => ({
    background: "#1f5185",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontFamily: "Roboto",
}));
const gutterX = { xs: 2, sm: 3, md: 6, lg: 16 };
type BoldWordProps = {
    children: React.ReactNode;
};

export function BoldWord({ children }: BoldWordProps) {
    return (
        <Typography
            component="span"
            sx={{
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: "#1F3252",

            }}
        >
            {children}
        </Typography>
    );
}

function Main() {
    const navigate = useNavigate();

    return (
        <Grid container>
            {/*HERO SECTION*/}
            <Grid
                container
                lg={12}
                className="about-header"
                sx={{
                    paddingBottom: 4,
                    px: gutterX,
                    marginTop: 6,
                    backgroundAttachment: 'fixed',
                    zIndex: 1,
                    borderRadius: 3,
                    borderColor: "#e3e3e3",
                    borderWidth: 1,
                    borderStyle: "solid",
                    overflow: "hidden",
                }}
            >
                {/* Left: Image (4 columns) */}
                <Grid
                    item
                    xs={12}
                    lg={4}
                    sx={{
                        mt: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "center", lg: "flex-start" },
                       // p: { xs: 3, lg: 4 },
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 180, sm: 240, lg: 300 },
                            height: { xs: 180, sm: 240, lg: 300 },
                            borderRadius: "50%",
                            overflow: "hidden",
                            border: "1px solid rgba(227,227,227,0.9)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                            backgroundColor: "rgba(255,255,255,0.06)",
                        }}
                    >
                        <img
                            src={selfiePic}
                            alt="Aggrey Lelei"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    </Box>
                </Grid>

                {/* Right: Glassy Hero (8 columns) */}
                <Grid
                    item
                    xs={12}
                    lg={8}
                    sx={{
                        mt: 10,
                        //p: { xs: 3, lg: 4 },
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            width: "100%",
                            borderRadius: 3,
                            border: "1px solid rgba(227,227,227,0.65)",
                            backgroundColor: "rgba(194,194,220,0.22)", // tweak if your background is light/dark
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            p: { xs: 3, sm: 4 },
                        }}
                    >
                        <Stack spacing={1.2}>
                            {/* Name */}
                            <Typography  variant="h4" sx={{ fontWeight: 500 }}>
                                Aggrey Lelei
                            </Typography>

                            {/* Title */}
                            <StyledTypography variant="h5" sx={{ fontWeight: 600 }}>
                                Head of Tech
                            </StyledTypography>

                            {/* Bio */}
                            <Typography
                                variant="h6"
                                className="article-text"
                                sx={{
                                    color: "#1F2A44 !important",
                                    fontWeight: `450 !important`,
                                    maxWidth: 760,
                                    lineHeight: 1.65,
                                }}
                            >
                                I'm a Tech Lead specializing in Spring Boot enterprise
                                systems and distributed architecture. I Built and maintain a
                                cloud-native real-time chat, voice call + notification workflow, handling
                                multiple concurrent peak users with event-based coordination,
                                backpressure-aware processing, and production monitoring.
                            </Typography>

                            {/* Buttons */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ pt: 1 }}>
                                {/* Resume – primary */}
                                <Button
                                    variant="contained"
                                    startIcon={<DescriptionIcon />}
                                    href="/resume"
                                    target="_blank"
                                     sx={{
                                         backgroundColor: "white",
                                         background: "rgba(31,81,133,0.24)",
                                          transition: "all 0.18s ease",
                                          "&:hover": {
                                             background: "#fff",
                                          },
                                     }}
                                >
                                    View Resume
                                </Button>

                                <Button
                                    target="_blank"
                                    variant="outlined"
                                    startIcon={<GitHubIcon />}
                                    href="https://github.com/hashedgrey"
                                    sx={{
                                        transition: "all 0.18s ease",
                                        "&:hover": {
                                            borderColor: "#1f5185",
                                            color: "#1f5185",
                                            backgroundColor: "rgba(31,81,133,0.12)",
                                        },
                                    }}
                                >
                                    GitHub
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<LinkedInIcon />}
                                    href="https://www.linkedin.com/in/aggrey-lelei"
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{
                                        transition: "all 0.18s ease",
                                        "&:hover": {
                                            borderColor: "#1f5185",
                                            color: "#1f5185",
                                            backgroundColor: "rgba(31,81,133,0.12)",
                                        },
                                    }}
                                >
                                    LinkedIn
                                </Button>
                            </Stack>


                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
            {/*ABOUT SECTION*/}

            <Grid
                item
                xs={12}
                sx={{
                    px: gutterX,
                    mt: 3,
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "#1F51853D",
                        p: { xs: 3, sm: 4 },
                        borderTopRightRadius: 24,
                        borderBottomLeftRadius: 24,
                        borderTopLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        lineHeight: 1.65,
                    }}
                >
                    <Typography variant="h5"
                                sx={{ fontWeight: 650,
                                    mb: 1,
                                    color: "#1A2C49 !important",
                                    fontSize: { xs: "1.6rem", sm: "1.75rem", md: "1.9rem" },
                                }}>
                        About Aggrey
                    </Typography>

                    <Typography
                        className='article-text'
                        variant="body1"
                                sx={{
                                    color: "#20365A !important",
                                    fontSize: "1rem !important",
                                    //fontweight: 500,
                                    //fontFamily: "calibri",
                                    //lineHeight: 1.65,
                                }}
                    >
                        Currently the <BoldWord> Head of Technology  at Tiret Resorts </BoldWord>. I lead a team of engineers
                        and own the technical direction, architecture, and delivery of the cloud-native
                        communication and logistics platform used by NGOs to coordinate regional operations.
                        <br/>
                        <BoldWord>Under my management the platform increased NGO productivity and regional engagement
                            by ~55%, supporting multiple concurrent users with sub-150 ms response times under peak load.
                        </BoldWord>
                        <br />
                        The system delivers real-time chat, voice calls, secure notifications, and intelligent
                        summarization, built on event-driven coordination, backpressure-aware processing, and
                        production-grade monitoring. I design and operate <BoldWord>Spring Boot–based distributed services
                    </BoldWord> with clear boundaries and strong observability.
                        <br />
                        I also integrate Embabel to enable agentic and retrieval-augmented workflows grounded in
                        live system data, while leading hiring, mentoring, and architectural decisions to ensure
                        the platform scales reliably.
                    </Typography>
                </Box>
            </Grid>

        </Grid>
    );
}

export default Main;
